import React, { useContext, useState } from 'react'
import styled, { css } from 'styled-components'
import { useIntl } from 'gatsby-plugin-intl'
import { breakpoint } from 'src/theme/grid'
import ExchangeOffersViewModeContext from 'src/features/stocks/components/map/ExchangeOffersViewModeContext'
import FiltersMenu, { ActionMenu as FiltersActionMenu } from './FiltersMenu'
import Tabs from './Tabs'
import { OfferAgeFilter } from './OfferAgeFilter'
import { useOfferAgeFilter } from './OfferAgeFilterContext'

const FiltersRow = ({
  className,
  children,
  advancedFilters,
  activeFiltersNumber,
  clearFilters,
  createFilterTab,
  removeFilterTab,
  tabs,
  tabsData,
  activeTab,
  setActiveTab
}) => {
  const [moreFiltersOpened, setOpenMoreFilters] = useState(false)
  const [mobileDrawerOpened, setMobileDrawerOpened] = useState(false)
  const intl = useIntl()
  const { range, setRange } = useOfferAgeFilter()
  const filtersCount = activeFiltersNumber + (range === 'all' ? 0 : 1)
  const handleClearFilters = () => {
    setRange('all')
    clearFilters()
  }
  const viewSwitch = useContext(ExchangeOffersViewModeContext)
  return (
    <div className={className}>
      <Tabs
        createFilterTab={createFilterTab}
        removeFilterTab={removeFilterTab}
        list={tabs}
        tabsData={tabsData}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />
      <DisplayDesktop>
        {children}
        {moreFiltersOpened && (
          <>
            {advancedFilters}
            <OfferAgeFilter id='exchange-offer-age-desktop' />
          </>
        )}
        <ShowMoreDesktop
          onClick={() => setOpenMoreFilters(!moreFiltersOpened)}
          opened={moreFiltersOpened}
        >
          <span>
            {moreFiltersOpened
              ? `- ${intl.formatMessage({ id: 'common.less' })}`
              : `+ ${intl.formatMessage({ id: 'common.more' })}`}
          </span>
        </ShowMoreDesktop>
        {viewSwitch && <DesktopViewSwitch>{viewSwitch}</DesktopViewSwitch>}
        <FiltersMenu createFilterTab={createFilterTab} />
      </DisplayDesktop>
      <MobileViewActions>
        {viewSwitch}
        <FiltersMenu createFilterTab={createFilterTab} />
      </MobileViewActions>
      <FilterMobileButton
        type='button'
        onClick={() => setMobileDrawerOpened(true)}
      >
        <span>{intl.formatMessage({ id: 'common.filter' })}</span>
        <i>{filtersCount}</i>
      </FilterMobileButton>
      {mobileDrawerOpened && (
        <>
          <MobileDrawer>
            <DrawerContent>
              <DrawerTitle>
                {intl.formatMessage({ id: 'common.filters' })}
                <FiltersMenu
                  createFilterTab={createFilterTab}
                  closeMobileDrawer={() => setMobileDrawerOpened(false)}
                />
              </DrawerTitle>
              <FilterBoxesWrapper>
                {children}
                {advancedFilters}
                <OfferAgeFilter id='exchange-offer-age-mobile' />
              </FilterBoxesWrapper>
              <ActionRow>
                <CancelButton
                  type='button'
                  onClick={() => setMobileDrawerOpened(false)}
                >
                  {intl.formatMessage({ id: 'common.cancel' })}
                </CancelButton>
                <AcceptButton
                  type='button'
                  onClick={() => setMobileDrawerOpened(false)}
                >
                  {intl.formatMessage({ id: 'common.filter' })}{' '}
                  <span>{filtersCount}</span>
                </AcceptButton>
                <ClearFilters type='button' onClick={handleClearFilters}>
                  {intl.formatMessage({ id: 'offers.clearFilters' })}
                </ClearFilters>
              </ActionRow>
            </DrawerContent>
          </MobileDrawer>
        </>
      )}
    </div>
  )
}

export default styled(FiltersRow)`
  display: inline-block;
  width: 100%;
  box-sizing: border-box;
  margin: 0.6rem 0;
`

const ShowMoreDesktop = styled.span`
  display: inline-block;
  vertical-align: middle;
  font-size: 1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  cursor: pointer;
  transition: color 0.3s;
  margin-bottom: 0.6rem;
  &:hover {
    color: ${({ theme }) => theme.colors.easternBlue.darken().hex()};
    & > span {
      background-color: ${({ theme }) => theme.colors.athensGray.hex()};
      border-radius: 4px;
    }
  }
  & > span {
    display: inline-block;
    position: relative;
    height: 36px;
    line-height: 36px;
    vertical-align: middle;
    padding: 0 0.6rem;
  }
`

const DisplayDesktop = styled.section`
  display: none;
  position: relative;
  width: 100%;
  box-sizing: border-box;
  padding-right: 110px;
  ${breakpoint.m`
      display: inline-block;
      width: 100%;
  `}

  & > div {
    display: inline-block;
    margin-right: 0.6rem;
    margin-bottom: 0.6rem;
    vertical-align: middle;
  }
`

const DesktopViewSwitch = styled.span`
  display: none;
  ${breakpoint.m`
    position: absolute;
    top: 3px;
    right: 42px;
    display: inline-flex;
  `}
`

const MobileViewActions = styled.div`
  position: fixed;
  right: 1rem;
  bottom: 1.6rem;
  z-index: 98;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 0.6rem;

  & > ${FiltersMenu} {
    position: relative;
    top: auto;
    right: auto;
  }

  ${FiltersActionMenu} {
    top: auto;
    bottom: calc(100% + 0.6rem);
    right: 0;
    max-width: calc(100vw - 2rem);
  }

  ${breakpoint.m`
    display: none;
  `}
`

const MobileDrawer = styled.section`
  display: flex;
  flex-direction: column;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: white;
  z-index: 200;
  box-shadow: 0 2px 10px 0
    ${({ theme }) => theme.colors.ebonyClay.alpha(0.15).hex()};
  border-top: 1px solid ${({ theme }) => theme.colors.iron.hex()};
  border-radius: 12px;
  box-sizing: border-box;
  &:before {
    content: '';
    width: 100vh;
    height: 100vw;
    background-color: ${({ theme }) => theme.colors.white.alpha(0.4).hex()};
    position: absolute;
    top: -60px;
    left: 0;
  }
  ${breakpoint.m`
      display: none;
  `}
`

const DrawerContent = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  padding: 1rem;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  overflow: hidden;
  z-index: 200;
`

const DrawerTitle = styled.span`
  display: inline-block;
  flex-shrink: 0;
  font-size: 1.05rem;
  color: ${({ theme }) => theme.colors.black.hex()};
  line-height: 2;
  font-weight: 800;
  margin-bottom: 2.2rem;
  position: relative;
`

const mobileButtonFocus = css`
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.easternBlue.hex()};
    outline-offset: 2px;
  }
`

const FilterMobileButton = styled.button`
  display: flex;
  align-items: center;
  border: 0;
  padding: 0;
  width: 150px;
  height: 64px;
  line-height: 64px;
  border-radius: 32px;
  box-shadow: 0 0 20px 0
    ${({ theme }) => theme.colors.ebonyClay.alpha(0.15).hex()};
  background-color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  color: white;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  ${mobileButtonFocus}
  position: fixed;
  bottom: 1.6rem;
  left: -40px;
  overflow: hidden;
  z-index: 98;
  & > span {
    display: inline-block;
    margin-left: 50px;
  }
  & > i {
    display: inline-block;
    position: absolute;
    right: 17px;
    top: 50%;
    transform: translateY(-50%);
    width: 30px;
    height: 30px;
    line-height: 30px;
    vertical-align: middle;
    text-align: center;
    font-style: normal;
    color: white;
    background-color: ${({ theme }) => theme.colors.apple.hex()};
    border-radius: 50%;
  }
  ${breakpoint.m`
      display: none !important;
  `}
`

const ActionRow = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.6rem;
  align-items: center;
  flex-shrink: 0;
  background-color: white;
  width: 100%;
  box-sizing: border-box;
  font-weight: 600;
  font-size: 0.875rem;
  z-index: 200;
  padding-top: 1rem;
  padding-bottom: env(safe-area-inset-bottom, 0px);
`

const CancelButton = styled.button`
  height: 44px;
  border: 1px solid ${({ theme }) => theme.colors.ghost.hex()};
  border-radius: 4px;
  background-color: white;
  color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  font: inherit;
  cursor: pointer;
  ${mobileButtonFocus}
`

const AcceptButton = styled.button`
  display: flex;
  flex-direction: row;
  background-color: ${({ theme }) => theme.colors.ebonyClay.hex()};
  align-items: center;
  border-radius: 4px;
  justify-content: center;
  height: 44px;
  border: 0;
  box-sizing: border-box;
  color: white;
  padding: 0 0.8rem 0 1.4rem;
  font: inherit;
  cursor: pointer;
  ${mobileButtonFocus}
  & > span {
    display: flex;
    justify-content: center;
    align-items: center;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.apple.hex()};
    margin-left: 0.6rem;
  }
`

const FilterBoxesWrapper = styled.div`
  display: block;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 1.2rem;
`

const ClearFilters = styled.button`
  display: inline-block;
  grid-column: 1 / -1;
  text-align: center;
  width: 100%;
  box-sizing: border-box;
  color: ${({ theme }) => theme.colors.easternBlue.hex()};
  font-weight: 400;
  font-size: 0.85rem;
  font-family: inherit;
  min-height: 36px;
  border: 0;
  background-color: transparent;
  padding: 0;
  cursor: pointer;
  ${mobileButtonFocus}
`
